import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import useModal from '@/hooks/useModal';
import { Passport, ShareMode, TalkFlowShare } from '@/types/api';
import { Button, Stack } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import PassportItem from '@/components/Record/SubjectContent/PassportItem';
import ShareDetailButton from '@/components/Record/ShareButton/ShareDetailButton';
import { roleToFilter } from '@/components/Record/util/function';
import { getNestedDataFetcher } from '@/utils/apis';
import { PASSPORT_BASE_URL } from '@/constant/apiList';
import EmptyContent from '@/components/@IntranetLibrary/EmptyContent';
import {
  useSetTalkFlowShareMode,
  useTalkFlowShareList,
} from '@/hooks/apiHooks';
import { CommentInputRef } from '@/components/Comment/CommentInput';
import { useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

export type MentionPermissionModalRef = {
  open: (passportIds: string[]) => Promise<void>;
  close: () => void;
};

export type MentionPermissionModalProps = {
  hasSharePermission: boolean;
  shareList: TalkFlowShare[];
  docId: string;
  addComment: CommentInputRef['submit'];
  addCommentOnSuccess?: () => Promise<void>;
};

const MentionPermissionModal = forwardRef<
  MentionPermissionModalRef,
  MentionPermissionModalProps
>((props, ref) => {
  const {
    hasSharePermission,
    shareList,
    docId,
    addComment,
    addCommentOnSuccess,
  } = props;
  const { t } = useTranslation();

  const { showError } = useSnackbar();
  const { mutate } = useTalkFlowShareList(docId);

  const [initialState, setInitialState] = useState<
    'loading' | 'error' | 'ready'
  >('loading');
  const [mentionedPassport, setMentionedPassport] = useState<Passport[]>([]);
  const [idsWithSharePermission, setIdsWithSharePermission] = useState<
    string[]
  >([]);
  const [idsWithCommentPermission, setIdsWithCommentPermission] = useState<
    string[]
  >([]);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setMentionedPassport([]);
    setIdsWithSharePermission([]);
    setIdsWithCommentPermission([]);
    setLoading(false);
    setInitialState('loading');
    mutate();
  };

  const modalRef = useRef<ModalRef>(null);
  const { openModal, closeModal } = useModal(modalRef, undefined, reset);

  const title = useMemo(() => {
    if (hasSharePermission) {
      return t(
        '멘션하신 사용자에게 이 문서를 볼 권한이 없습니다. 댓글 등록 시 아래 인원에게 문서가 공유됩니다.',
      );
    } else {
      return t(
        '멘션하신 사용자에게 이 문서의 조회 권한이 없습니다. 해당 사용자에게는 알림이 전달되지 않으며, 멘션이 일반 텍스트로 표시됩니다.',
      );
    }
  }, [hasSharePermission]);

  const primaryButtonTitle = useMemo(() => {
    if (hasSharePermission) {
      return t('공유 후 등록');
    } else {
      return t('댓글 등록');
    }
  }, [hasSharePermission]);

  const primaryButtonOnClick = useCallback(async () => {
    // 권한 없으면 그냥 등록
    if (!hasSharePermission) {
      await addComment();
      closeModal();
      return;
    }

    try {
      setLoading(true);

      //1. 옵션별 권한 부여
      const payload = [
        ...idsWithCommentPermission.map((passportId) => {
          return {
            role: 'reader',
            scope: {
              type: 'passport',
              value: passportId,
            },
          };
        }),
        ...idsWithSharePermission.map((passportId) => {
          return {
            role: 'sharer',
            scope: {
              type: 'passport',
              value: passportId,
            },
          };
        }),
      ];

      await useSetTalkFlowShareMode(docId, { aclData: payload });

      //2. 댓글 등록
      await addComment();
      await addCommentOnSuccess?.();
      closeModal();
    } catch (e) {
      let message = t('문서 공유에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      if (e instanceof AxiosError) {
        if (e.response?.data?.message) {
          message = e.response?.data?.message;
        }
      }

      showError({
        message,
      });
    } finally {
      setLoading(false);
    }
  }, [
    docId,
    hasSharePermission,
    shareList,
    addComment,
    addCommentOnSuccess,
    idsWithCommentPermission,
    idsWithSharePermission,
  ]);

  const handleChangeOptions = useCallback(
    (mode: ShareMode, passportId: string) => {
      if (mode === ShareMode.Share) {
        setIdsWithSharePermission((prev) => {
          if (prev.includes(passportId)) return [...prev]; // 이미 있어도 새 배열 반환
          return [...prev, passportId];
        });
        setIdsWithCommentPermission((prev) =>
          prev.filter((id) => id !== passportId),
        );
      } else {
        setIdsWithSharePermission((prev) =>
          prev.filter((id) => id !== passportId),
        );
        setIdsWithCommentPermission((prev) => {
          if (prev.includes(passportId)) return [...prev]; // 이미 있어도 새 배열 반환
          return [...prev, passportId];
        });
      }
    },
    [],
  );

  useImperativeHandle(ref, () => ({
    open: async (passportIds: string[]) => {
      openModal();

      //passportIds로 passport 정보 가져오기
      try {
        const data = await Promise.all(
          passportIds.map((id) =>
            getNestedDataFetcher<Passport>(`${PASSPORT_BASE_URL}/${id}`),
          ),
        );

        setMentionedPassport(data);
        setInitialState('ready');

        if (hasSharePermission) {
          setIdsWithSharePermission(data.map(({ passportId }) => passportId));
        }
      } catch (e) {
        console.error(e);
        setInitialState('error');
      }
    },
    close: closeModal,
  }));

  return (
    <Modal
      ref={modalRef}
      title={''}
      size={'small'}
      handleClose={closeModal}
      sx={{
        '.modal-body': { overflow: 'visible', paddingBottom: '0' },
        '.small': {
          minWidth: '400px !important',
        },
        '.modal-footer': {
          backgroundColor: 'var(--dk-bg, white)',
          paddingTop: '0',
          button: { minWidth: '120px' },
        },
      }}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap="8px" mt={'24px'}>
          <Button
            variant={'outlined'}
            size={'large'}
            disabled={loading}
            onClick={closeModal}
          >
            {t('취소')}
          </Button>
          <Button
            variant={'contained'}
            size={'large'}
            loading={loading}
            onClick={primaryButtonOnClick}
          >
            {primaryButtonTitle}
          </Button>
        </Stack>
      }
    >
      <Stack gap="24px">
        <Body1 sx={{ whiteSpace: 'pre-line', fontWeight: 400 }}>{title}</Body1>
        <Stack
          sx={{
            maxWidth: '640px',
            padding: '12px',
            gap: '8px',
            borderRadius: '8px',
            background: 'var(--dk-bg-subtle, #f9fafb)',
          }}
        >
          {/*  로딩 */}
          {initialState === 'loading' && (
            <>
              {Array.from({ length: 3 }, (v, i) => i).map((key) => (
                <Skeleton
                  key={key}
                  variant={'rect'}
                  width={'100%'}
                  height={'38px'}
                />
              ))}
            </>
          )}

          {initialState === 'error' && (
            <EmptyContent title={t('에러가 발생했습니다.')} type={'error'} />
          )}

          {initialState === 'ready' && (
            <>
              {mentionedPassport.map((res) => {
                const { passportId, detail } = res;
                return (
                  <PassportItem
                    key={`share-${passportId}`}
                    passport={{
                      passportId,
                      ...detail,
                    }}
                    disabled={loading}
                    rightContents={
                      hasSharePermission ? (
                        <ShareDetailButton
                          modeOptions={[ShareMode.Share, ShareMode.Comment]}
                          shareMode={roleToFilter(
                            idsWithSharePermission.includes(passportId)
                              ? 'sharer'
                              : '',
                          )}
                          onClick={(mode) =>
                            handleChangeOptions(mode, passportId)
                          }
                          disabled={loading}
                        />
                      ) : null
                    }
                  />
                );
              })}
            </>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
});

MentionPermissionModal.displayName = 'MentionPermissionModal';

export default MentionPermissionModal;
